import { set } from "@ember/object"
import Service, { inject as service } from "@ember/service"
import { isNone } from "@ember/utils"
import * as Sentry from "@sentry/browser"
import { blankUnifiedLessonAttributes } from "@shared/lib/card-stack/blank-lesson"
import { cloneCardStackAttributesWithNewIds } from "@shared/lib/card-stack/clone"
import { emptyCardStackExtracted } from "@shared/lib/card-stack/extracted"
import { emptyProseMirrorContent } from "@shared/lib/card-stack/prose-mirror-utils"
import { isUnifiedCardStack } from "@shared/lib/card-stack/unified-migration"
import type {
  CardStack,
  CardStackAttributes,
  CardStackSummary,
  LegacyCardStackAttributes,
  ParentCardStack,
} from "@shared/models/card-stack"
import type { Permission } from "@shared/models/permissions"
import { objectId } from "@shared/util/object-id"
import { reduce } from "lodash-es"
import type CardStackTemplateUpdater from "./card-stack-template-updater"
import type EmberStore from "./ember-store"

export default class PhantomCardStacks extends Service {
  @service("ember-store") store!: EmberStore
  @service socket
  @service cardStackTemplateUpdater!: CardStackTemplateUpdater

  dateToCardStack = Object.create(null) // so it's in dictionary mode for perfs
  cardStackIdToOwnerId = Object.create(null) // so we can see if a card stack id is already part of a course/planbook
  cardStackIdToCardStackTemplateId = Object.create(null)
  cardStackIdCardCache = Object.create(null)

  /**
   * We look for the owner (course or planbook) to see who owns this card stack
   * This helps us know if the owner has changed
   * @param cardStackId
   */
  getOwnerIdForCardStackId(cardStackId) {
    return this.cardStackIdToOwnerId[cardStackId]
  }

  /**
   * If the coure or planbook doesn't have a cardStack for a date, we
   * need to create one. This creates it, adds it to the local memory,
   * and then returns it
   *
   * @param type planbook or course
   * @param ownerId the id of the planbook or course
   * @param usedAs  wehther it's a lesson or routine
   * @param dateString
   * @param defaultPermissions the permissions we need to assign by default
   */
  findCardStackForDate(
    ownerId: string,
    usedAs: "lesson" | "routine",
    dateString: string,
    defaultPermissions: Array<Permission>,
    relationships: Array<[string, string]>,
    unifiedLessons: boolean,
    useCardStackSummary: boolean,
  ): CardStack {
    let type = useCardStackSummary ? "card-stack-summary" : "card-stack"
    // Fetch the things
    let cardStackId = this.findCardStackIdForDate(ownerId, dateString)
    let cardStack = this.store.findInMemory(type, cardStackId)

    // if, in fact, we don't have it, we create it and insert it locally
    if (cardStack === null || cardStack === undefined) {
      // Convert the relationships array to a form for the cardStack
      let relationshipsMap = reduce(
        relationships,
        (acc, [type, id]) => {
          acc[type] = { data: { type, id } }
          return acc
        },
        {},
      )
      this.store.insertDocument({
        id: cardStackId,
        type: type,
        attributes: useCardStackSummary
          ? this._createCardStackSummaryAttributes(usedAs)
          : this._createCardStackAttributes(usedAs, unifiedLessons),
        relationships: relationshipsMap,
        meta: {
          isNewLesson: true,
          permissions: defaultPermissions,
          cardsUpdatingFromTemplate: true,
        },
      })
      cardStack = this.store.findInMemory(type, cardStackId)

      // We make this subscribe call b/c if it's a new lesson, we didn't subscribe to the ID we created
      // so, if this computed property is hit and we end up in this branch, it means it's
      // a lesson that was already created or one that was just created. Since the `subscribe`
      // call is idempotent, it's not a problem to hit it multiple times.
      this.socket.subscribe(type, cardStackId)
    }

    // Give it back.
    return cardStack
  }

  /**
   * If there's a template and the cardStack is updating from it,
   * we need to add the template's cards/proseMirror to the cardStack.
   */
  async applyTemplateCardsToCardStackIfNeeded(
    cardStackId: string | null,
    cardStackTemplateId: string | null,
  ) {
    // Find the card stack locally
    let cardStack = this.store.findInMemory("card-stack", cardStackId) as CardStack | undefined
    if (!cardStack) return

    // Does it need cards? Just return if so
    let cardStackNeedsCards =
      cardStack.meta.cardsUpdatingFromTemplate ||
      // If the cards/proseMirror are non-null they're set. If they're null, we use the template.
      // Check cards even for UnifiedCardStacks in case it is migrated but unedited.
      (!isNone(cardStackTemplateId) &&
        cardStack.attributes.cards === null &&
        !(isUnifiedCardStack(cardStack) && cardStack.attributes.proseMirror !== null))

    if (cardStackNeedsCards !== true) return

    // If we have a template, we should add the template's cards/proseMirror/extracted.
    if (cardStackTemplateId && cardStackId) {
      // set up watcher to update the cardStackTemplate cards when it changes
      this.cardStackIdToCardStackTemplateId[cardStackId] = cardStackTemplateId
      this.cardStackTemplateUpdater.subscribe(cardStackId, cardStackTemplateId)

      const cardStackTemplate: CardStack | undefined = await this.store.find(
        "card-stack",
        cardStackTemplateId,
      )
      if (cardStack.meta.cardsUpdatingFromTemplate !== true && cardStack.attributes.cards !== null)
        return
      if (cardStackTemplate) {
        // Set cardStack's cards/proseMirror/extracted from cardStackTemplate.
        const parentCardStack: ParentCardStack = {
          id: cardStackTemplate.id,
          version: cardStackTemplate.attributes.version,
          ownerIds: null,
          courseId: null,
          planbookId: null,
          source: "template",
        }
        const newAttrs = cloneCardStackAttributesWithNewIds(
          cardStackTemplate.attributes,
          parentCardStack,
          isUnifiedCardStack(cardStack),
          {
            overrideTitle: cardStack.attributes.title || "",
            mapCardId: (cardId) => {
              // Give each card a fresh id, stable between applications.
              const cardLookupId = `${cardStackId}${cardId}`
              const newId = (this.cardStackIdCardCache[cardLookupId] =
                this.cardStackIdCardCache[cardLookupId] || objectId())
              return newId
            },
            onMigrationError: (error) => Sentry.captureException(error),
          },
        )

        // Ember-set the attributes we actually need.
        set(cardStack.attributes, "proseMirror", newAttrs.proseMirror)
        set(cardStack.attributes, "extracted", newAttrs.extracted)
        set(cardStack.attributes, "cards", newAttrs.cards)
        set(cardStack.attributes, "parentCardStack", newAttrs.parentCardStack)

        cardStack.meta.cardsUpdatingFromTemplate = true
        return
      } else {
        Sentry.withScope(function (scope) {
          scope.setLevel(Sentry.Severity.Error)
          scope.setExtra("CardStackTemplateId", cardStackTemplateId)
          scope.setExtra("cardStackId", cardStackId)
          Sentry.captureMessage("CardStackTemplate is undefined.")
        })
        // Fall through to missing template case.
      }
    }

    // No or not-found template => set the cards to an empty array []
    if (isUnifiedCardStack(cardStack)) {
      set(cardStack.attributes, "proseMirror", emptyProseMirrorContent())
      set(cardStack.attributes, "extracted", emptyCardStackExtracted())
    }
    set(cardStack.attributes, "cards", [])
  }

  /**
   * We create a cardStackId for a routine or lesson and then cache it
   * so the next time we request it ƒor that date, we get the same id
   * @param type a planbook or a course
   * @param typeId
   * @param dateString
   * @returns id
   */
  findCardStackIdForDate(ownerId: string, dateString: string): string {
    let compositeId = `${ownerId}:${dateString}`
    let cardStackId = this.dateToCardStack[compositeId]
    // We check the owner in case the lesson got moved to a different course
    let ownerOfCardStack = this.cardStackIdToOwnerId[cardStackId]
    let hasDifferentOwner = ownerOfCardStack !== undefined && ownerOfCardStack !== ownerId
    if (cardStackId && !hasDifferentOwner) {
      return cardStackId
    } else {
      let id = objectId()
      // We record the new owner so we can tell if it changes in the future
      this.cardStackIdToOwnerId[id] = ownerId
      this.dateToCardStack[compositeId] = id
      return id
    }
  }

  /**
   * We need to invalidate a date. For instance, if the date is moved.
   *
   */
  invalidateDate(ownerId: string, cardStackId: string | null, dateString: string): void {
    let compositeId = `${ownerId}:${dateString}`
    // First, we invalidate the cardStackId to ownerId so we can say the owner changed
    if (cardStackId !== null) this.cardStackIdToOwnerId[cardStackId] = undefined
    // Then, we invalidate the dateToCardStack array
    // should these both be set to undefined? Probably?
    this.dateToCardStack[compositeId] = null
  }

  private _createCardStackAttributes(
    usedAs: "lesson" | "routine",
    unifiedLessons: boolean,
  ): CardStackAttributes {
    if (unifiedLessons && usedAs === "lesson") {
      return blankUnifiedLessonAttributes()
    } else {
      return {
        title: null,
        color: null,
        parentCardStack: null,
        version: 0,
        versionNarrative: {},
        _revision: 0,
        _revisedBySessionId: null,
        schemaVersion: 1,
        usedAs: usedAs,
        cards: null,
        commentIds: [],
        proseMirror: null,
        extracted: null,
      } satisfies LegacyCardStackAttributes
    }
  }

  private _createCardStackSummaryAttributes(
    usedAs: "lesson" | "routine",
  ): CardStackSummary["attributes"] {
    return {
      title: null,
      color: null,
      usedAs: usedAs,
      _revision: null,
    }
  }
}
